<template>
    <ElDialog
        v-addIframe="{ forceAdd: true }"
        :visible="true"
        :width="'640px'"
        :title="$t('patent-view.tabSettingDialog.title')"
        :appendToBody="true"
        :closeOnClickModal="false"
        :closeOnPressEscape="false"
        class="dual-view-setting-dialog"
        @close="cancel"
    >
        <div class="dual-view-setting-dialog__container">
            <!--body-->
            <div class="dual-view-setting-dialog__body">
                <!--左侧-->
                <div class="dual-view-setting-dialog__side">
                    <div class="dual-view-setting-dialog__side-title">
                        <span>{{ $t('patent-view.tabSettingDialog.left') }}</span>
                        <span>({{ currentSelected.left.length }}/{{ leftSideFields.length }})</span>
                    </div>
                    <Draggable
                        v-model="leftSideFields"
                        :options="dragOptions"
                        class="dual-view-setting-dialog__side-body"
                        element="div"
                    >
                        <transition-group :name="'flip-list'" type="transition">
                            <div v-for="item in leftSideFields" :key="`left-${item.name}`" class="dual-view-setting-dialog__side-item">
                                <div class="dual-view-setting-dialog__side-item-container">
                                    <ElCheckbox v-model="item.is_selected" :disabled="item.is_default">
                                        {{ item.label }}
                                    </ElCheckbox>
                                    <p :class="{ 'dual-view-setting-dialog__side-item-default': true, 'dual-view-setting-dialog__side-item-default--set': item.is_default }" @click="setDefaultTab('left', item.name)">
                                        {{ item.is_default ? $t('patent-view.tabSettingDialog.default') : $t('patent-view.tabSettingDialog.setAsDefault') }}
                                    </p>
                                </div>
                                <PtIcon icon="SolidMove" class="dual-view-setting-dialog__side-item-drag" />
                            </div>
                        </transition-group>
                    </Draggable>
                </div>
                <!--右侧-->
                <div class="dual-view-setting-dialog__side">
                    <div class="dual-view-setting-dialog__side-title">
                        <span>{{ $t('patent-view.tabSettingDialog.right') }}</span>
                        <span>({{ currentSelected.right.length }}/{{ rightSideFields.length }})</span>
                    </div>
                    <Draggable
                        v-model="rightSideFields"
                        :options="dragOptions"
                        class="dual-view-setting-dialog__side-body"
                        element="div"
                    >
                        <transition-group :name="'flip-list'" type="transition">
                            <div v-for="item in rightSideFields" :key="`right-${item.name}`" class="dual-view-setting-dialog__side-item">
                                <div class="dual-view-setting-dialog__side-item-container">
                                    <ElCheckbox v-model="item.is_selected" :disabled="item.is_default">
                                        {{ item.label }}
                                    </ElCheckbox>
                                    <p :class="{ 'dual-view-setting-dialog__side-item-default': true, 'dual-view-setting-dialog__side-item-default--set': item.is_default }" @click="setDefaultTab('right', item.name)">
                                        {{ item.is_default ? $t('patent-view.tabSettingDialog.default') : $t('patent-view.tabSettingDialog.setAsDefault') }}
                                    </p>
                                </div>
                                <PtIcon icon="SolidMove" class="dual-view-setting-dialog__side-item-drag" />
                            </div>
                        </transition-group>
                    </Draggable>
                </div>
            </div>
            <!--footer-->
            <div class="dual-view-setting-dialog__footer">
                <button
                    :disabled="isSubmitting"
                    data-size="big"
                    data-type="default"
                    class="pt-ui-btn dual-view-setting-dialog__button"
                    @click="cancel"
                >
                    {{ $t('snapUi.dialog.cancel') }}
                </button>
                <button
                    :disabled="isSubmitting"
                    data-size="big"
                    type="primary"
                    class="pt-ui-btn dual-view-setting-dialog__button"
                    @click="submit"
                >
                    {{ $t('snapUi.dialog.submit') }}
                </button>
            </div>
        </div>
    </ElDialog>
</template>

<script>
import { Dialog, Checkbox } from 'element-ui';
import { Icon } from 'patsnap-biz';
import Draggable from 'vuedraggable';
import _ from 'lodash';
import { DUAL_VIEW_SETTING_DEFAULT_LIST, DUAL_VIEW_SETTING_DEFAULT_DISPLAY } from '../../utils/dual-view-setting';
import { getFilteredManifestList } from '@/subview_plugins/install';
import { setAnalyticsDualViewSetting } from 'library-@/apis/core_basic_api/view';
import addIframe from '../../directives/addIframe';
import '@patsnap-ui/icon/assets/solid/move.svg';
import { useDualViewStates } from '@/composables/use-dual-view-states';
import { usePermission } from '@/composables/use-permission';

export default {
    name: 'DualViewSettingDialog',
    components: {
        ElDialog: Dialog,
        ElCheckbox: Checkbox,
        PtIcon: Icon,
        Draggable,
    },
    directives: {
        addIframe,
    },
    setup() {
        const { dualViewSetting, updateDualViewSetting } = useDualViewStates();

        const { permissions } = usePermission();

        return {
            permissions,
            dualViewSetting,
            updateDualViewSetting,
        };
    },
    data() {
        return {
            dragOptions: {
                animation: 150,
                forceFallback: true,
                ghostClass: 'dual-view-setting-dialog__side-item--moving',
                chosenClass: 'dual-view-setting-dialog__side-item--chosen',
                handle: '.dual-view-setting-dialog__side-item-drag',
            },
            leftSideFields: [],
            rightSideFields: [],
            isSubmitting: false,
        };
    },
    computed: {
        currentSelected() {
            const left = this.leftSideFields.filter(item => item.is_selected);
            const right = this.rightSideFields.filter(item => item.is_selected);
            return { left, right };
        },
    },
    created() {
        this.transferFieldForDisplay();
    },
    methods: {
        setDefaultTab(part, name) {
            const list = part === 'left' ? this.leftSideFields : this.rightSideFields;
            list.forEach(item => {
                item.is_default = (item.name === name);
                if (!item.is_selected && (item.name === name)) item.is_selected = true;
            });
        },
        transferFieldForDisplay() {
            const dualViewSetting = _.cloneDeep(this.dualViewSetting);
            const isKwicAvailable = window.self !== window.top && !!this.$route.query.q;
            const allLeftFields = getFilteredManifestList('left', this.$store)
                .filter(mf => mf.name !== 'kwic' || isKwicAvailable)
                .map(mf => ({
                    name: mf.name,
                    label: this.$t(mf.label),
                    aclCheckKey: mf.aclCheckKey,
                    is_selected: DUAL_VIEW_SETTING_DEFAULT_LIST.left.includes(mf.name),
                    is_default: DUAL_VIEW_SETTING_DEFAULT_DISPLAY.left === mf.name,
                }));
            const allRightFields = getFilteredManifestList('right', this.$store)
                .map(mf => ({
                    name: mf.name,
                    label: this.$t(mf.label),
                    aclCheckKey: mf.aclCheckKey,
                    is_selected: DUAL_VIEW_SETTING_DEFAULT_LIST.right.includes(mf.name),
                    is_default: DUAL_VIEW_SETTING_DEFAULT_DISPLAY.right === mf.name,
                }));

            const userLeftFields = _.get(dualViewSetting, 'left', []);
            const userRightFields = _.get(dualViewSetting, 'right', []);

            const finalLeftFields = [];
            const finalRightFields = [];

            userLeftFields.forEach(item => {
                const matchedIndexInOriginField = allLeftFields.findIndex(({ name }) => item.name === name);
                if (matchedIndexInOriginField !== -1) {
                    const matchedItemInOriginField = allLeftFields[matchedIndexInOriginField];
                    finalLeftFields.push({
                        ...item,
                        label: matchedItemInOriginField.label,
                        aclCheckKey: matchedItemInOriginField.aclCheckKey,
                    });
                    matchedItemInOriginField.needToDelete = true;
                }
            });
            userRightFields.forEach(item => {
                const matchedIndexInOriginField = allRightFields.findIndex(({ name }) => item.name === name);
                if (matchedIndexInOriginField !== -1) {
                    const matchedItemInOriginField = allRightFields[matchedIndexInOriginField];
                    finalRightFields.push({
                        ...item,
                        label: matchedItemInOriginField.label,
                        aclCheckKey: matchedItemInOriginField.aclCheckKey,
                    });
                    matchedItemInOriginField.needToDelete = true;
                }
            });

            const allLeftFieldsAfterMerge = allLeftFields.filter(item => !item.needToDelete);
            const allRightFieldsAfterMerge = allRightFields.filter(item => !item.needToDelete);

            this.leftSideFields = [...finalLeftFields, ...allLeftFieldsAfterMerge].filter(item => !item.aclCheckKey || (item.aclCheckKey && this.permissions[item.aclCheckKey]));
            this.rightSideFields = [...finalRightFields, ...allRightFieldsAfterMerge].filter(item => !item.aclCheckKey || (item.aclCheckKey && this.permissions[item.aclCheckKey]));
        },
        cancel() {
            this.$emit('cancel');
        },
        async submit() {
            const request = { left: this.leftSideFields, right: this.rightSideFields };
            this.isSubmitting = true;
            await setAnalyticsDualViewSetting(request);
            this.isSubmitting = false;
            this.updateDualViewSetting(request);
            this.$emit('submit', request);
        },
    },
};
</script>

<style lang="scss">
.dual-view-setting-dialog {
    .dual-view-setting-dialog__container {
        color: $text-primary;
    }
    .dual-view-setting-dialog__body {
        border-radius: 4px;
        border: 1px solid $border-normal;
        display: flex;
        height: 370px;
        position: relative;
        &::after {
            content: '';
            height: 100%;
            width: 1px;
            background-color: $border-normal;
            position: absolute;
            top: 0;
            left: 50%;
        }
        .dual-view-setting-dialog__side {
            height: 100%;
            width: 50%;
            padding: 8px 4px 8px 8px;
            display: flex;
            flex-direction: column;
            .dual-view-setting-dialog__side-title {
                padding: 0 8px;
                margin-bottom: 8px;
                line-height: 24px;
                flex-shrink: 0;
                span:first-of-type {
                    color: $text-primary;
                    font-size: 14px;
                    font-weight: bold;
                }
                span:last-of-type {
                    color: $text-hint;
                    font-size: 12px;
                    font-weight: 400;
                    margin-left: 4px;
                }
            }
            .dual-view-setting-dialog__side-body {
                flex: 1;
                overflow: auto;
                padding-right: 4px;
            }
            .dual-view-setting-dialog__side-item {
                padding: 0 32px 0 8px;
                height: 32px;
                line-height: 32px;
                border-radius: 4px;
                margin-bottom: 4px;
                position: relative;
                &:hover {
                    background-color: $bg-hover;
                }
                &:last-of-type {
                    margin-bottom: 0;
                }
                &--moving {
                    background-color: $bg-hover;
                    border: 1px dashed $border-normal;
                }
                .dual-view-setting-dialog__side-item-container {
                    height: 100%;
                    width: 100%;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    .dual-view-setting-dialog__side-item-default {
                        cursor: pointer;
                        color: $status-blue-primary;
                        display: none;
                        flex-shrink: 0;
                        line-height: 24px;
                        &--set {
                            cursor: default;
                            color: $text-secondary;
                            display: inline-block;
                        }
                    }
                    &:hover {
                        .dual-view-setting-dialog__side-item-default {
                            display: inline-block;
                        }
                    }
                }
                .dual-view-setting-dialog__side-item-drag {
                    cursor: pointer;
                    position: absolute;
                    right: 8px;
                    top: 50%;
                    transform: translateY(-50%);
                    -ms-transform: translateY(-50%);
                    font-size: 16px;
                    fill: #505F79;
                }
            }
        }
    }
    .dual-view-setting-dialog__footer {
        margin-top: 24px;
        text-align: right;
        font-size: 0;
        .dual-view-setting-dialog__button {
            min-width: 140px;
            margin-right: 8px;
            &:last-of-type {
                margin-right: 0;
            }
        }
    }
    /*el覆盖*/
    .el-checkbox {
        margin-right: 0;
        display: flex;
        align-items: center;
        flex: 1;
        overflow: hidden;
        .el-checkbox__input {
            height: 14px;
            width: 14px;
            line-height: 14px !important;
            .el-checkbox__inner {
                height: 14px !important;
                width: 14px !important;
                &::after {
                    top: 0;
                    left: 3px;
                }
            }
        }
        .el-checkbox__label {
            line-height: 24px;
            color: $text-primary !important;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        &.is-checked.is-disabled {
            .el-checkbox__input {
                .el-checkbox__inner {
                    background-color: $status-blue-disable !important;
                    border-color: $status-blue-disable !important;
                    &::after {
                        border-color: #fff !important;
                    }
                }
            }
        }
    }
}
.flip-list-move {
    transition: transform 0.5s;
}
</style>
